import React, { useState, useRef, useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Button, Typography, IconButton } from '@mui/material';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { SectionTab } from './tab-button';
import { t } from '../config';

// ── Shared helper: add a text element to canvas ──
const addTextToCanvas = (store, opts) => {
  const page = store.activePage;
  if (!page) return;

  const scaleFactor = Math.max(store.width / 500, 0.5);
  const fontSize = Math.round((opts.fontSize || 36) * scaleFactor);
  const width = Math.round((opts.width || 350) * scaleFactor);

  const defaults = {
    type: 'text',
    x: store.width / 2 - width / 2,
    y: store.height / 2 - fontSize / 2,
    width,
    text: opts.text || 'Cool text',
    fontSize,
    fontFamily: opts.fontFamily || 'Roboto',
    fontWeight: opts.fontWeight || 'normal',
    fontStyle: opts.fontStyle || 'normal',
    fill: '#000000',
    align: 'center',
  };
  defaults.height = Math.max(fontSize * 1.5, 40);

  page.addElement(defaults);
  store.history.addUndoState();
};

// ── Predefined text templates (json = element data, preview = thumbnail image) ──
const TEXT_TEMPLATES = [
  { json: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-adventure-fun-cursive.json', preview: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-adventure-fun-cursive.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-celebration-birthday-cursive-handwritting.json', preview: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-celebration-birthday-cursive-handwritting.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-corporate-business-clean-simple-modern.json', preview: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-corporate-business-clean-simple-modern.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-corporate-hiring-simple-clean-modern.json', preview: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-corporate-hiring-simple-clean-modern.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-fashion-sale-brand-clothes.json', preview: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-fashion-sale-brand-clothes.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-modern-minamalistic-simple.json', preview: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-modern-minamalistic-simple.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-party-invitation.json', preview: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-party-invitation.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-price-package-corporate-list.json', preview: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-price-package-corporate-list.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-quote-contemporary.json', preview: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-quote-contemporary.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-quote-quotation-motivation-inspiring.json', preview: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-quote-quotation-motivation-inspiring.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-romantic-handwrittern-cursive-elegant-celebration.json', preview: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-romantic-handwrittern-cursive-elegant-celebration.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-sale-fashion-brand.json', preview: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-sale-fashion-brand.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-sassy-fun-feminine.json', preview: 'https://api.polotno.com/text-templates/2021-06-01-text-plain-sassy-fun-feminine.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-02-text-color-ancient-old-roman-decorative.json', preview: 'https://api.polotno.com/text-templates/2021-06-02-text-color-ancient-old-roman-decorative.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-02-text-color-awesome-fun-intresting-funky-playful.json', preview: 'https://api.polotno.com/text-templates/2021-06-02-text-color-awesome-fun-intresting-funky-playful.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-02-text-color-beautiful-decorative-love-elgant-cursive-emotion-famenine.json', preview: 'https://api.polotno.com/text-templates/2021-06-02-text-color-beautiful-decorative-love-elgant-cursive-emotion-famenine.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-02-text-color-buy-corporate-urgent-now-marketing.json', preview: 'https://api.polotno.com/text-templates/2021-06-02-text-color-buy-corporate-urgent-now-marketing.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-02-text-color-classic-west-cowboy-wildwest-country-classic.json', preview: 'https://api.polotno.com/text-templates/2021-06-02-text-color-classic-west-cowboy-wildwest-country-classic.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-02-text-color-future-ftureistic-trendy-funky-fun-cool.json', preview: 'https://api.polotno.com/text-templates/2021-06-02-text-color-future-ftureistic-trendy-funky-fun-cool.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-02-text-color-halloween-celeration-horror-spooky.json', preview: 'https://api.polotno.com/text-templates/2021-06-02-text-color-halloween-celeration-horror-spooky.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-02-text-color-marry-christmas-celebration.json', preview: 'https://api.polotno.com/text-templates/2021-06-02-text-color-marry-christmas-celebration.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-02-text-color-motivating-motivational-inspiring-inspirational-decorative.json', preview: 'https://api.polotno.com/text-templates/2021-06-02-text-color-motivating-motivational-inspiring-inspirational-decorative.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-02-text-color-motivation-inspiration-cursive-decorative.json', preview: 'https://api.polotno.com/text-templates/2021-06-02-text-color-motivation-inspiration-cursive-decorative.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-02-text-color-orange-fruity-fruit-food-fuun-happy.json', preview: 'https://api.polotno.com/text-templates/2021-06-02-text-color-orange-fruity-fruit-food-fuun-happy.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-02-text-color-romantic-elegant-love.json', preview: 'https://api.polotno.com/text-templates/2021-06-02-text-color-romantic-elegant-love.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-02-text-plain-data-stat-statistics-numeric-figures-reports-charts.json', preview: 'https://api.polotno.com/text-templates/2021-06-02-text-plain-data-stat-statistics-numeric-figures-reports-charts.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-02-text-plain-list-colum-modern-point-fact.json', preview: 'https://api.polotno.com/text-templates/2021-06-02-text-plain-list-colum-modern-point-fact.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-02-text-plain-recipe-food-list.json', preview: 'https://api.polotno.com/text-templates/2021-06-02-text-plain-recipe-food-list.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-04-text-color-ancient-medieval-old-roman-kind-royal.json', preview: 'https://api.polotno.com/text-templates/2021-06-04-text-color-ancient-medieval-old-roman-kind-royal.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-04-text-color-anonymous-mystery-suspense.json', preview: 'https://api.polotno.com/text-templates/2021-06-04-text-color-anonymous-mystery-suspense.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-04-text-color-autumn-red-orange-season-cursive-decorative.json', preview: 'https://api.polotno.com/text-templates/2021-06-04-text-color-autumn-red-orange-season-cursive-decorative.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-04-text-color-colorful-color-fun-beads-game-themed-videogames.json', preview: 'https://api.polotno.com/text-templates/2021-06-04-text-color-colorful-color-fun-beads-game-themed-videogames.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-04-text-color-decorative-hollow-effect.json', preview: 'https://api.polotno.com/text-templates/2021-06-04-text-color-decorative-hollow-effect.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-04-text-color-decorative-message-line-lineart.json', preview: 'https://api.polotno.com/text-templates/2021-06-04-text-color-decorative-message-line-lineart.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-04-text-color-fairytale-fanatcy-magic-magical.json', preview: 'https://api.polotno.com/text-templates/2021-06-04-text-color-fairytale-fanatcy-magic-magical.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-04-text-color-happy-easter-festive-holiday.json', preview: 'https://api.polotno.com/text-templates/2021-06-04-text-color-happy-easter-festive-holiday.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-04-text-color-happy-fun-joy-celebration.json', preview: 'https://api.polotno.com/text-templates/2021-06-04-text-color-happy-fun-joy-celebration.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-04-text-color-horror-scary-fun-celebration-halloween.json', preview: 'https://api.polotno.com/text-templates/2021-06-04-text-color-horror-scary-fun-celebration-halloween.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-04-text-color-inivitation-rsvp-event-celebration-weeding.json', preview: 'https://api.polotno.com/text-templates/2021-06-04-text-color-inivitation-rsvp-event-celebration-weeding.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-04-text-color-motivation-hope-never-giveup-win-wivtory-cool-funky.json', preview: 'https://api.polotno.com/text-templates/2021-06-04-text-color-motivation-hope-never-giveup-win-wivtory-cool-funky.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-04-text-color-murder-horror-scary-temed-holloween-haunted-suspence.json', preview: 'https://api.polotno.com/text-templates/2021-06-04-text-color-murder-horror-scary-temed-holloween-haunted-suspence.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-04-text-color-music-simple-straight-jazzy-simple.json', preview: 'https://api.polotno.com/text-templates/2021-06-04-text-color-music-simple-straight-jazzy-simple.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-04-text-color-numeric-number-figure-chalk-written-writting.json', preview: 'https://api.polotno.com/text-templates/2021-06-04-text-color-numeric-number-figure-chalk-written-writting.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-04-text-color-save-the-date-celebration-calander-inviation-event.json', preview: 'https://api.polotno.com/text-templates/2021-06-04-text-color-save-the-date-celebration-calander-inviation-event.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-04-text-color-save-the-date-invitation-party-invited-cool-.json', preview: 'https://api.polotno.com/text-templates/2021-06-04-text-color-save-the-date-invitation-party-invited-cool-.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-04-text-color-signature-sign-elegant-royal.json', preview: 'https://api.polotno.com/text-templates/2021-06-04-text-color-signature-sign-elegant-royal.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-04-text-color-spring-season-nature-leaf-flower-decorative.json', preview: 'https://api.polotno.com/text-templates/2021-06-04-text-color-spring-season-nature-leaf-flower-decorative.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-04-text-color-summer-sunny-hot-sea-season.json', preview: 'https://api.polotno.com/text-templates/2021-06-04-text-color-summer-sunny-hot-sea-season.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-04-text-color-treandu-cool-funky-hip.json', preview: 'https://api.polotno.com/text-templates/2021-06-04-text-color-treandu-cool-funky-hip.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-04-text-color-what-when-where-column-fun-cool-question.json', preview: 'https://api.polotno.com/text-templates/2021-06-04-text-color-what-when-where-column-fun-cool-question.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-04-text-color-winter-cold-wonderland-decorative-season.json', preview: 'https://api.polotno.com/text-templates/2021-06-04-text-color-winter-cold-wonderland-decorative-season.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-05-text-color-alert-sale-brand-clearance-shop-now-sjopping-discount.json', preview: 'https://api.polotno.com/text-templates/2021-06-05-text-color-alert-sale-brand-clearance-shop-now-sjopping-discount.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-05-text-color-chalk-drawing-chalkboard-pencil-marker-color-fill-hand-drawn-drawing.json', preview: 'https://api.polotno.com/text-templates/2021-06-05-text-color-chalk-drawing-chalkboard-pencil-marker-color-fill-hand-drawn-drawing.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-05-text-color-code-matrix-numbers-numeric-data-sale.json', preview: 'https://api.polotno.com/text-templates/2021-06-05-text-color-code-matrix-numbers-numeric-data-sale.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-05-text-color-fun-cool-cartoon-animated-game.json', preview: 'https://api.polotno.com/text-templates/2021-06-05-text-color-fun-cool-cartoon-animated-game.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-05-text-color-funky-cool-hashtag-young-fun.json', preview: 'https://api.polotno.com/text-templates/2021-06-05-text-color-funky-cool-hashtag-young-fun.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-05-text-color-instagram-tag-tagging-cool-unique.json', preview: 'https://api.polotno.com/text-templates/2021-06-05-text-color-instagram-tag-tagging-cool-unique.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-05-text-color-s-sle-black-friday-shopping-buy.json', preview: 'https://api.polotno.com/text-templates/2021-06-05-text-color-s-sle-black-friday-shopping-buy.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-05-text-color-speed-fast-run-running-cool-themed-cartoon-lines-waves-move-movement.json', preview: 'https://api.polotno.com/text-templates/2021-06-05-text-color-speed-fast-run-running-cool-themed-cartoon-lines-waves-move-movement.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-05-text-color-sweat-candy.json', preview: 'https://api.polotno.com/text-templates/2021-06-05-text-color-sweat-candy.png' },
  { json: 'https://api.polotno.com/text-templates/2021-06-05-text-color-themed-ink-bubble-fun-cool-cartoon-comic.json', preview: 'https://api.polotno.com/text-templates/2021-06-05-text-color-themed-ink-bubble-fun-cool-cartoon-comic.png' },
];

// ── Preload all template fonts in background (runs once) ──
let _fontsPreloaded = false;
function preloadTemplateFonts(store) {
  if (_fontsPreloaded) return;
  _fontsPreloaded = true;
  // Fetch all JSONs in parallel, collect font families, load them
  Promise.all(
    TEXT_TEMPLATES.map((t) =>
      fetch(t.json).then((r) => r.json()).catch(() => null)
    )
  ).then((results) => {
    const fonts = new Set();
    results.forEach((data) => {
      if (!data?.pages?.[0]?.children) return;
      data.pages[0].children.forEach((child) => {
        if (child.fontFamily) fonts.add(child.fontFamily);
      });
    });
    fonts.forEach((f) => store.loadFont(f));
  });
}

// ── Tab 1: Text presets + predefined templates ──
const TextTab = observer(({ store }) => {
  const [loadingIdx, setLoadingIdx] = useState(null);

  // Preload all template fonts on first mount
  useEffect(() => { preloadTemplateFonts(store); }, [store]);

  const presets = [
    {
      label: t('sidePanel.headerText', 'Create header'),
      text: t('sidePanel.headerText', 'Create header'),
      fontSize: 48,
      width: 400,
      fontWeight: 'bold',
      fontFamily: 'Roboto',
    },
    {
      label: t('sidePanel.subHeaderText', 'Create sub header'),
      text: t('sidePanel.subHeaderText', 'Create sub header'),
      fontSize: 28,
      width: 350,
      fontWeight: '500',
      fontFamily: 'Roboto',
    },
    {
      label: t('sidePanel.bodyText', 'Create body text'),
      text: t('sidePanel.bodyText', 'Create body text'),
      fontSize: 16,
      width: 300,
      fontWeight: 'normal',
      fontFamily: 'Roboto',
    },
  ];

  const handleTemplateClick = useCallback(async (tmpl, idx) => {
    const page = store.activePage;
    if (!page) return;
    setLoadingIdx(idx);
    try {
      const res = await fetch(tmpl.json);
      const data = await res.json();
      const templateChildren = data?.pages?.[0]?.children || [];

      // Add elements immediately so the user sees instant feedback
      const newIds = [];
      const fonts = new Set();
      templateChildren.forEach((child) => {
        const { id: _omit, ...childData } = child;
        const el = page.addElement(childData, { skipSelect: true });
        if (el) newIds.push(el.id);
        if (child.fontFamily) fonts.add(child.fontFamily);
      });
      if (newIds.length > 0) {
        store.selectElements(newIds);
      }
      store.history.addUndoState();

      // Load fonts in background — text-element's loadingdone handler
      // will reflow text once each font arrives
      [...fonts].forEach((f) => store.loadFont(f));
    } catch (err) {
      console.error('Failed to load text template:', err);
    } finally {
      setLoadingIdx(null);
    }
  }, [store]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {presets.map((preset, idx) => (
        <Button
          key={idx}
          fullWidth
          onClick={() => addTextToCanvas(store, preset)}
          sx={{
            justifyContent: 'center',
            textTransform: 'none',
            py: 0,
            px: 0,
            fontSize: preset.fontSize > 30 ? 30 : preset.fontSize > 20 ? 20 : 16,
            fontWeight: preset.fontWeight,
            fontFamily: preset.fontFamily,
            color: '#333',
            '&:hover': {
              backgroundColor: 'rgba(13,131,205,0.04)',
            },
          }}
        >
          {preset.label}
        </Button>
      ))}

      {/* Predefined text templates grid */}
      <Typography
        variant="caption"
        sx={{ mt: 2, mb: 1, color: '#888', fontSize: 11, fontWeight: 500 }}
      >
        {t('sidePanel.textTemplates', 'Text templates')}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 1,
        }}
      >
        {TEXT_TEMPLATES.map((tmpl, idx) => (
          <Box
            key={idx}
            onClick={() => handleTemplateClick(tmpl, idx)}
            sx={{
              cursor: loadingIdx === idx ? 'wait' : 'pointer',
              borderRadius: '4px',
              overflow: 'hidden',
              border: '1px solid transparent',
              opacity: loadingIdx === idx ? 0.5 : 1,
              transition: 'border-color 0.15s, box-shadow 0.15s, opacity 0.15s',
              '&:hover': {
                borderColor: '#0d83cd',
                boxShadow: '0 1px 4px rgba(13,131,205,0.15)',
              },
            }}
          >
            <img
              src={tmpl.preview}
              alt="Text template"
              loading="lazy"
              crossOrigin="anonymous"
              draggable={false}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
});

// ── Module-level font storage (persists until page refresh) ──
let _uploadedFonts = [];
let _fontIdCounter = 0;

const registerFont = (displayName, blobUrl) => {
  const style = document.createElement('style');
  style.textContent = `@font-face { font-family: '${displayName}'; src: url('${blobUrl}'); }`;
  document.head.appendChild(style);
  return { familyName: displayName, styleEl: style };
};

// ── Tab 2: My Fonts ──
const MyFontsTab = observer(({ store }) => {
  const [, setRenderKey] = useState(0);
  const forceUpdate = useCallback(() => setRenderKey((k) => k + 1), []);
  const fileInputRef = useRef(null);

  const fonts = _uploadedFonts;

  const handleUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const rawName = file.name.replace(/\.(ttf|otf|woff2?|eot)$/i, '');
    const blobUrl = URL.createObjectURL(file);
    const { familyName, styleEl } = registerFont(rawName, blobUrl);

    _uploadedFonts = [
      ..._uploadedFonts,
      { id: `${Date.now()}_${++_fontIdCounter}`, displayName: rawName, familyName, blobUrl, styleEl },
    ];
    forceUpdate();
  }, [forceUpdate]);

  const handleDelete = useCallback((id) => {
    const font = _uploadedFonts.find((f) => f.id === id);
    if (font) {
      if (font.styleEl?.parentNode) font.styleEl.parentNode.removeChild(font.styleEl);
      URL.revokeObjectURL(font.blobUrl);
    }
    _uploadedFonts = _uploadedFonts.filter((f) => f.id !== id);
    forceUpdate();
  }, [forceUpdate]);

  const handleFontClick = useCallback((font) => {
    addTextToCanvas(store, {
      text: 'Cool text',
      fontSize: 36,
      width: 350,
      fontFamily: font.familyName,
      fontWeight: 'normal',
    });
  }, [store]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Upload button */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".ttf,.otf,.woff,.woff2,.eot"
        style={{ display: 'none' }}
        onChange={handleUpload}
      />
      <Button
        fullWidth
        variant="outlined"
        onClick={() => fileInputRef.current?.click()}
        startIcon={<FileUploadOutlinedIcon />}
        sx={{
          textTransform: 'none',
          borderColor: '#ccc',
          color: '#333',
          fontWeight: 400,
          fontSize: 13,
          py: 0.8,
          '&:hover': { borderColor: '#999', backgroundColor: 'rgba(0,0,0,0.02)' },
        }}
      >
        {t('sidePanel.uploadFont', 'Upload font')}
      </Button>

      {/* Uploaded fonts list */}
      {fonts.map((font) => (
        <Box
          key={font.id}
          onClick={() => handleFontClick(font)}
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#b0b0b0',
            borderRadius: '6px',
            py: 2.5,
            px: 2,
            cursor: 'pointer',
            minHeight: 60,
            '&:hover': { backgroundColor: '#a0a0a0' },
          }}
        >
          <Typography
            sx={{
              fontFamily: `'${font.familyName}', sans-serif`,
              fontSize: 20,
              color: '#fff',
              textAlign: 'center',
              userSelect: 'none',
              lineHeight: 1.3,
            }}
          >
            {font.displayName} text
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); handleDelete(font.id); }}
            sx={{
              position: 'absolute',
              bottom: 4,
              right: 4,
              color: 'rgba(255,255,255,0.7)',
              '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.15)' },
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
});

// ── Main panel with tabs ──
const TextPanelContent = observer(({ store }) => {
  const [activeTab, setActiveTab] = useState('text');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Tab bar */}
      <Box sx={{ display: 'flex', borderBottom: '1px solid #e0e0e0' }}>
        {[
          { key: 'text', label: t('sidePanel.textTab', 'Text') },
          { key: 'myfonts', label: t('sidePanel.myFontsTab', 'My fonts') },
        ].map((tab) => (
          <Box
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            sx={{
              flex: 1,
              textAlign: 'center',
              py: 1,
              fontSize: 13,
              fontWeight: activeTab === tab.key ? 600 : 400,
              color: activeTab === tab.key ? '#0d83cd' : '#666',
              borderBottom: activeTab === tab.key ? '2px solid #0d83cd' : '2px solid transparent',
              cursor: 'pointer',
              userSelect: 'none',
              transition: 'color 0.15s, border-color 0.15s',
              '&:hover': { color: '#0d83cd' },
            }}
          >
            {tab.label}
          </Box>
        ))}
      </Box>

      {/* Tab content */}
      <Box sx={{ p: 2, flex: 1, overflowY: 'auto' }}>
        {activeTab === 'text' && <TextTab store={store} />}
        {activeTab === 'myfonts' && <MyFontsTab store={store} />}
      </Box>
    </Box>
  );
});

export const TextSection = {
  name: 'text',
  Tab: (props) => (
    <SectionTab name={t('sidePanel.text', 'Text')} {...props}>
      <TextFieldsIcon />
    </SectionTab>
  ),
  Panel: TextPanelContent,
};
